import { read, utils, type WorkBook, writeFileXLSX } from 'xlsx-js-style';
import type {
  INovaAppointment,
  INovaCarrierData,
  INovaCompany,
  INovaOrg,
  INovaPersistedWarehouse,
  INovaDock,
  INovaLoadType,
  INovaUser,
  INovaWarehouse
} from '@/interfaces/nova';
import type { GenericArrayOfObjects } from '@/interfaces/api';
import { NovaApiService } from '@/services/nova/NovaApiService';
import { createSheet, readSheetRows } from '@/services/workbookService';
import { keyCompaniesByName, systemNotify } from '@/services/util';
import {
  SUCCESS_IMPORT_MESSAGE,
  ALREADY_EXISTS_MESSAGE,
  getErrorMessage,
  getFailureStatusMessage,
  type IImportSession,
  type ImportProgress
} from './importShared';
import { importWarehouses, updateWarehouseCustomFields } from './importWarehouses';
import { importLoadTypes } from './importLoadTypes';
import { importDocks, updateMaptsDockSchedule } from './importDocks';
import { importAppointments } from './importAppointments';
import { createCarriers, inviteUsers } from './importPeople';

const COMPANY_LIMIT = 150000;

export interface IImportParams {
  file: File;
  token: string;
  org: INovaOrg;
  progress: ImportProgress;
  onAbort: () => void;
}

type ImportContext = {
  apiService: NovaApiService;
  org: INovaOrg;
  orgId: string;
  progress: ImportProgress;
  onAbort: () => void;
  session: IImportSession;
  workbookToImport: WorkBook;
  workbookReport: WorkBook;
  existingWarehouses: INovaPersistedWarehouse[];
};

async function wrapUp(progress: ImportProgress, workbookReport: WorkBook) {
  progress.clear();
  await progress.zoom(99, 5);
  progress.update('All done!', 100);
  setTimeout(() => {
    progress.setDialogVisibility(false);
    progress.update('', 0);
  }, 2000);

  writeFileXLSX(workbookReport, `report.xlsx`);
}

async function getSelectedUserOrgId(apiService: NovaApiService, onAbort: () => void) {
  let loggedInAsUserResponse;
  try {
    loggedInAsUserResponse = await apiService.getLoggedInAsUser();
  } catch (e) {
    onAbort();
    throw new Error(`Could not log in as selected user: ${getErrorMessage(e)}`, { cause: e });
  }
  return loggedInAsUserResponse.data[0]?.orgId;
}

/** Retrieves the selected user's org ID and verifies it matches the selected org. */
async function verifySelectedUserOrg(
  apiService: NovaApiService,
  org: INovaOrg,
  onAbort: () => void
) {
  try {
    const orgId = await getSelectedUserOrgId(apiService, onAbort);
    if (orgId !== org.id) {
      throw new Error(
        getFailureStatusMessage({ message: "Org ID does not match selected User's Org ID" })
      );
    }
    return orgId as string;
  } catch (e) {
    onAbort();
    throw new Error(getErrorMessage(e), { cause: e });
  }
}

async function fetchCompaniesKeyedByName(apiService: NovaApiService, onAbort: () => void) {
  try {
    const companyRes = await apiService.getCompanies({ limit: COMPANY_LIMIT });
    return keyCompaniesByName(companyRes.data);
  } catch (e) {
    onAbort();
    throw new Error(getErrorMessage(e), { cause: e });
  }
}

async function muteOrgAppointmentNotifications({
  apiService,
  org,
  orgId,
  onAbort,
  session
}: ImportContext) {
  // Set before the request: a failed PATCH may still have been applied server side.
  session.notificationsMuteAttempted = true;
  try {
    const orgUpdateResponse = await apiService.updateOrg(orgId, {
      ...org,
      settings: { ...(org.settings ?? {}), muteAppointmentNotifications: true }
    });
    if (!orgUpdateResponse.data?.[0]?.settings?.muteAppointmentNotifications) {
      onAbort();
      throw new Error(
        getFailureStatusMessage({
          message: 'Muting Org Appointment Notifications was unsuccessful'
        })
      );
    }
  } catch (e) {
    onAbort();
    throw new Error(getErrorMessage(e), { cause: e });
  }
}

async function inviteUsersIntoReport(
  context: ImportContext,
  users: INovaUser[],
  endingInterval: number
) {
  context.progress.createInterval('Inviting Users', endingInterval);
  const { usersReport } = await inviteUsers(context.apiService, users, context.existingWarehouses);
  createSheet(context.workbookReport, usersReport, 'Users');
}

type ImportSheets = {
  warehouses: INovaWarehouse[];
  users: INovaUser[];
  carriers: INovaCarrierData[];
};

/** Invites users and creates carriers; `finished` means there are no warehouses left to import. */
async function importUsersAndCarriers(context: ImportContext, sheets: ImportSheets) {
  const { apiService, workbookReport, progress, onAbort } = context;
  const { warehouses, users, carriers } = sheets;
  const skipWarehouseImports = warehouses.length === 0;
  const skipCarrierImports = carriers.length === 0;

  // User imports only - no warehouse or carrier data
  if (skipWarehouseImports && users.length > 0) {
    await inviteUsersIntoReport(context, users, skipCarrierImports ? 98 : 5);
    if (skipCarrierImports) {
      return { finished: true, companiesKeyedByName: {} };
    }
  }

  const companiesKeyedByName = await fetchCompaniesKeyedByName(apiService, onAbort);
  if (!skipCarrierImports) {
    progress.createInterval('Creating Carriers', skipWarehouseImports ? 98 : 10);
    const { carriersReport } = await createCarriers(apiService, carriers, companiesKeyedByName);
    createSheet(workbookReport, carriersReport, 'Carriers');
  }
  return { finished: skipWarehouseImports, companiesKeyedByName };
}

async function importWarehouseHierarchy(context: ImportContext, warehouses: INovaWarehouse[]) {
  const { apiService, orgId, progress, session, workbookToImport, workbookReport } = context;

  progress.createInterval('Importing Warehouses', 20);
  const { warehousesKeyedByExternalId } = await importWarehouses({
    warehouses,
    apiService,
    existingWarehouses: context.existingWarehouses,
    session
  });
  createSheet(workbookReport, session.warehouseReport, 'Warehouses');

  await progress.zoom(20);
  progress.createInterval('Importing Load Types', 30);
  const { loadTypeIdsKeyedByExternalId, loadTypesReport } = await importLoadTypes({
    orgId,
    loadTypes: readSheetRows<INovaLoadType>(workbookToImport, 'LoadTypes'),
    apiService,
    warehousesKeyedByExternalId
  });
  createSheet(workbookReport, loadTypesReport, 'LoadTypes');

  await progress.zoom(30);
  progress.createInterval('Importing Docks', 50);
  const { docksKeyedByExternalId, docksReport } = await importDocks({
    orgId,
    docks: readSheetRows<INovaDock>(workbookToImport, 'Docks'),
    apiService,
    loadTypeIdsKeyedByExternalId,
    warehousesKeyedByExternalId
  });
  createSheet(workbookReport, docksReport, 'Docks');

  return {
    warehousesKeyedByExternalId,
    loadTypeIdsKeyedByExternalId,
    docksKeyedByExternalId,
    docksReport
  };
}

async function importAppointmentsSheet(
  context: ImportContext,
  hierarchy: Awaited<ReturnType<typeof importWarehouseHierarchy>>,
  companiesKeyedByName: Record<string, INovaCompany>
) {
  const { apiService, orgId, progress, workbookToImport } = context;
  const appointments = readSheetRows<INovaAppointment>(workbookToImport, 'Appointments');
  if (!appointments.length) {
    return [];
  }

  await progress.zoom(50);
  progress.createInterval('Importing Appointments', 95);
  return importAppointments({
    orgId,
    appointments,
    apiService,
    companiesKeyedByName,
    loadTypeIdsKeyedByExternalId: hierarchy.loadTypeIdsKeyedByExternalId,
    warehousesKeyedByExternalId: hierarchy.warehousesKeyedByExternalId,
    docksKeyedByExternalId: hierarchy.docksKeyedByExternalId
  });
}

async function finishImport(
  context: ImportContext,
  users: INovaUser[],
  docksReport: INovaDock[],
  appointmentsReport: INovaAppointment[]
) {
  const { apiService, progress, workbookReport } = context;

  // Update dock schedules to original for newly imported docks only
  await progress.zoom(95);
  if (users.length > 0) {
    await inviteUsersIntoReport(context, users, 98);
  }
  progress.createInterval('Wrapping Up', 98);
  await Promise.all(
    docksReport
      .filter(dock => dock.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE && dock.id)
      .map(dock =>
        updateMaptsDockSchedule(apiService, dock, {
          version: 1,
          ...JSON.parse(dock.schedule as string)
        })
      )
  );

  createSheet(workbookReport, appointmentsReport, 'Appointments');
  await wrapUp(progress, workbookReport);
}

async function restoreNotificationSetting(
  { apiService, org }: Pick<ImportContext, 'apiService' | 'org'>,
  initialMuteNotificationsSetting: boolean
) {
  // Set appointment notification setting to original setting
  await apiService.updateOrg(org.id, {
    ...org,
    settings: {
      ...(org.settings ?? {}),
      muteAppointmentNotifications: initialMuteNotificationsSetting
    }
  });
}

async function restoreWarehouseCustomFields({
  apiService,
  session
}: Pick<ImportContext, 'apiService' | 'session'>) {
  // Update warehouse Custom Fields to original on newly imported warehouses only
  await Promise.all(
    session.warehouseReport
      .filter(warehouse => warehouse.wasImportedIntoNova === ALREADY_EXISTS_MESSAGE && warehouse.id)
      .map(async warehouse => {
        const fields = session.warehouseCustomFields?.[warehouse.id as string];
        // Only update warehouses where custom fields existed prior to import
        if (fields) {
          await updateWarehouseCustomFields(apiService, warehouse.id as string, fields);
        }
      })
  );

  session.warehouseCustomFields = null;
  session.warehouseReport = [];
}

function reportPartialImport(workbookToImport: WorkBook, workbookReport: WorkBook, e: unknown) {
  let errorMessage = '';
  if (Object.keys(workbookReport.Sheets)?.length) {
    errorMessage += 'PARTIAL IMPORT - ';
    writeFileXLSX({ ...workbookToImport, ...workbookReport }, `partial-import report.xlsx`);
  }
  errorMessage += getErrorMessage(e);
  systemNotify({
    message: errorMessage,
    type: 'error'
  });
}

async function runImport(
  {
    apiService,
    org,
    progress,
    onAbort
  }: Omit<IImportParams, 'file' | 'token'> & {
    apiService: NovaApiService;
  },
  session: IImportSession,
  workbookToImport: WorkBook,
  workbookReport: WorkBook
) {
  const orgId = await verifySelectedUserOrg(apiService, org, onAbort);

  const sheets: ImportSheets = {
    warehouses: readSheetRows<INovaWarehouse>(workbookToImport, 'Warehouses'),
    users: readSheetRows<INovaUser>(workbookToImport, 'Users'),
    carriers: readSheetRows<INovaCarrierData>(workbookToImport, 'Carriers')
  };
  if (!sheets.warehouses.length && !sheets.users.length && !sheets.carriers.length) {
    onAbort();
    throw new Error(getFailureStatusMessage({ message: 'No Data to Import' }));
  }

  const existingWarehousesRes = await apiService.getWarehouses();
  const context: ImportContext = {
    apiService,
    org,
    orgId,
    progress,
    onAbort,
    session,
    workbookToImport,
    workbookReport,
    existingWarehouses: existingWarehousesRes.data ?? []
  };

  const { finished, companiesKeyedByName } = await importUsersAndCarriers(context, sheets);
  if (finished) {
    await wrapUp(progress, workbookReport);
    return;
  }

  await muteOrgAppointmentNotifications(context);
  const hierarchy = await importWarehouseHierarchy(context, sheets.warehouses);
  const appointmentsReport = await importAppointmentsSheet(
    context,
    hierarchy,
    companiesKeyedByName
  );
  await finishImport(context, sheets.users, hierarchy.docksReport, appointmentsReport);
}

export async function importWorkbookToNova({ file, token, org, progress, onAbort }: IImportParams) {
  const session: IImportSession = {
    notificationsMuteAttempted: false,
    warehouseReport: [],
    warehouseCustomFields: null
  };
  progress.setDialogVisibility(true);

  const workbookToImport: WorkBook = read(await file.arrayBuffer());
  const workbookReport: WorkBook = utils.book_new();
  const initialMuteNotificationsSetting = org.settings?.muteAppointmentNotifications ?? false;
  const apiService = new NovaApiService({ token });

  try {
    await runImport(
      { apiService, org, progress, onAbort },
      session,
      workbookToImport,
      workbookReport
    );
  } catch (e) {
    onAbort();
    reportPartialImport(workbookToImport, workbookReport, e);
  } finally {
    if (session.notificationsMuteAttempted) {
      try {
        await restoreNotificationSetting({ apiService, org }, initialMuteNotificationsSetting);
      } catch (e) {
        systemNotify({
          message: `Restoring the Org's notification settings failed: ${getErrorMessage(e)}`,
          type: 'error'
        });
      }
    }
    try {
      await restoreWarehouseCustomFields({ apiService, session });
    } catch (e) {
      systemNotify({
        message: `Restoring the warehouse custom fields failed: ${getErrorMessage(e)}`,
        type: 'error'
      });
    }
  }
}
